import { CreateBatchJobRequest, BatchJob, BatchJobExecution, BatchJobIteration } from 'api/model.ts';

import instance from '../axios_instance';
import { FilterConfig } from '../../store/workflowExecutions.ts';
import { SortDirection } from '../../types.ts';

export const getBatchJobs = async (projectId: string): Promise<BatchJob[]> => {
  const response = await instance.get<BatchJob[]>(`/projects/${projectId}/batch-jobs`);
  return response.data;
};

type GetBatchJobsArgs = {
  projectId: string;
  pageParam?: number;
  pageSize?: number;
  sortDirection?: '' | '-';
  sortColumn?: string;
  filters?: FilterConfig[];
};

export const getBatchJobsPage = async ({
  projectId,
  pageParam,
  pageSize = 100,
  sortDirection = '-',
  sortColumn = 'name',
  filters,
}: GetBatchJobsArgs): Promise<BatchJob[]> => {
  const searchParams = new URLSearchParams({
    page: pageParam?.toString() ?? '1',
    page_size: pageSize.toString(),
    sort: sortDirection + sortColumn,
  });
  if (filters) {
    filters.forEach(
      (filter) => filter && searchParams.append('query', `${filter.attribute}[${filter.operator}]=${filter.value}`),
    );
  }
  const response = await instance.get<BatchJob[]>(`/projects/${projectId}/batch-jobs?${searchParams.toString()}`);
  return response.data;
};

export const getBatchJob = async (id: string): Promise<BatchJob> => {
  const response = await instance.get<BatchJob>(`/batch-jobs/${id}`);
  return response.data;
};

export const createBatchJob = async (projectId: string, batchJob: CreateBatchJobRequest): Promise<BatchJob> => {
  const response = await instance.post<BatchJob>(`/projects/${projectId}/batch-jobs`, batchJob);
  return response.data;
};

export const removeBatchJob = async (id: string) => {
  return await instance.delete(`/batch-jobs/${id}`);
};

export const executeBatchJob = async (id: string) => {
  return await instance.post(`/batch-jobs/${id}`);
};

export const updateBatchJob = async (
  projectId: string,
  id: string,
  batchJob: CreateBatchJobRequest,
): Promise<BatchJob> => {
  const response = await instance.put<BatchJob>(`/projects/${projectId}/batch-jobs/${id}`, batchJob);
  return response.data;
};

export const getBatchJobExecutions = async ({
  projectId,
  pageParam,
  pageSize = 100,
  batchJobId,
  sortColumn = 'trigger_time',
  sortDirection = '-',
}: {
  projectId: string;
  pageParam?: number;
  pageSize?: number;
  batchJobId?: string;
  sortDirection?: SortDirection;
  sortColumn?: keyof BatchJobExecution;
}): Promise<BatchJobExecution[]> => {
  const searchParams = new URLSearchParams({
    page: pageParam?.toString() ?? '1',
    page_size: pageSize.toString(),
    sort: sortDirection + sortColumn,
  });
  if (batchJobId) {
    searchParams.append('query', `batch_job_id[eq]=${batchJobId}`);
  }
  const response = await instance.get<BatchJobExecution[]>(
    `/projects/${projectId}/batch-job-executions?${searchParams.toString()}`,
  );
  return response.data;
};

export const getBatchJobIterations = async ({
  id,
  pageParam,
  pageSize = 100,
}: {
  id: string;
  pageParam?: number;
  pageSize?: number;
}): Promise<BatchJobIteration[]> => {
  const response = await instance.get<BatchJobIteration[]>(
    `/batch-job-executions/${id}/iterations?page=${pageParam}&page_size=${pageSize}`,
  );
  return response.data;
};
